import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  FieldType,
  FormField,
  FormSchema,
  FormSection,
  FormTemplateDetail,
} from '@foundation/shared';
import {
  FIELD_TYPES,
  FIELD_TYPE_LABEL_TH,
  FORM_SCOPE_LABEL_TH,
  validateSchema,
} from '@foundation/shared';
import { api } from '../lib/api';
import { formatDateTime, formatNumber } from '../lib/format';
import { ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

/**
 * ตัวแก้ไขแบบฟอร์ม
 *
 * ตรวจ schema ด้วย validateSchema ตัวเดียวกับที่ Worker ใช้ตอน publish
 * ผู้ดูแลจึงเห็นปัญหาทันทีที่แก้ ไม่ต้องรอกด publish แล้วโดนปฏิเสธ
 */
export default function FormEditor() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { allowed } = useAuth();

  const [form, setForm] = useState<FormTemplateDetail | null>(null);
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<{ sectionIndex: number; fieldIndex: number } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const f = await api<FormTemplateDetail>(`/forms/${id}`);
      setForm(f);
      setSchema(f.schema);
      setDirty(false);
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const issues = schema ? validateSchema(schema) : [];
  const readOnly = !form || form.status !== 'draft' || !allowed('form:write');

  function update(next: FormSchema) {
    setSchema(next);
    setDirty(true);
  }

  async function save() {
    if (!schema) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/forms/${id}`, { method: 'PATCH', body: { schema } });
      setNotice('บันทึกฉบับร่างแล้ว');
      setDirty(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!confirm('เผยแพร่แบบฟอร์มนี้?\nหลังเผยแพร่จะแก้ไขไม่ได้ ต้องสร้างเวอร์ชันใหม่แทน')) return;
    setBusy(true);
    setError(null);
    try {
      if (dirty) await api(`/forms/${id}`, { method: 'PATCH', body: { schema } });
      await api(`/forms/${id}/publish`, { method: 'POST' });
      setNotice('เผยแพร่เรียบร้อย — งานลงพื้นที่ใหม่จะใช้เวอร์ชันนี้');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function newVersion() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ id: string }>(`/forms/${id}/new-version`, { method: 'POST' });
      navigate(`/forms/${res.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (error && !form) return <ErrorAlert error={error} />;
  if (!form || !schema) return <Loading />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{form.name}</h1>
          <p>
            <span className="mono">{form.code}</span> · v{form.version} ·{' '}
            {FORM_SCOPE_LABEL_TH[form.scope as keyof typeof FORM_SCOPE_LABEL_TH] ?? form.scope} ·{' '}
            <span className={`badge ${form.status === 'published' ? 'green' : 'amber'}`}>
              {form.status === 'published' ? 'เผยแพร่แล้ว' : form.status === 'draft' ? 'ฉบับร่าง' : 'เก็บถาวร'}
            </span>
          </p>
        </div>
        <Link className="btn secondary" to="/forms">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      {form.status !== 'draft' && (
        <div className="alert info">
          แบบฟอร์มนี้{form.status === 'published' ? 'เผยแพร่แล้ว' : 'ถูกเก็บถาวร'} จึงแก้ไขไม่ได้
          — มีงานลงพื้นที่ {formatNumber(form.usageCount ?? 0)} รายการชี้มาที่เวอร์ชันนี้
          และต้องอ่านออกตลอดไป
          {allowed('form:write') && form.status === 'published' && (
            <>
              {' '}
              <button className="link" onClick={() => void newVersion()} disabled={busy}>
                สร้างเวอร์ชันใหม่เพื่อแก้ไข
              </button>
            </>
          )}
        </div>
      )}

      {issues.length > 0 && (
        <div className="alert error">
          <strong>ต้องแก้ก่อนเผยแพร่</strong>
          <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
            {issues.map((i, n) => (
              <li key={n} className="small">
                {i.path}: {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {schema.sections.map((section, si) => (
        <div className="card" key={si}>
          <div className="page-head" style={{ marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <input
                value={section.title}
                disabled={readOnly}
                aria-label="ชื่อส่วน"
                style={{ fontWeight: 700, fontSize: 16 }}
                onChange={(e) =>
                  update(patchSection(schema, si, { title: e.target.value }))
                }
              />
              <div className="btn-row" style={{ marginTop: 8 }}>
                <input
                  value={section.key}
                  disabled={readOnly}
                  aria-label="รหัสส่วน"
                  className="mono"
                  style={{ maxWidth: 180 }}
                  onChange={(e) => update(patchSection(schema, si, { key: e.target.value }))}
                />
                <label className="checkbox" style={{ alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={section.sensitive ?? false}
                    disabled={readOnly}
                    onChange={(e) =>
                      update(patchSection(schema, si, { sensitive: e.target.checked }))
                    }
                  />
                  <span className="small">ข้อมูลอ่อนไหว (ซ่อนเมื่อไม่มีความยินยอม)</span>
                </label>
              </div>
            </div>
            {!readOnly && schema.sections.length > 1 && (
              <button
                className="secondary"
                onClick={() =>
                  update({ ...schema, sections: schema.sections.filter((_, i) => i !== si) })
                }
              >
                ลบส่วนนี้
              </button>
            )}
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>รหัสฟิลด์</th>
                  <th className="wrap">คำถาม</th>
                  <th>ชนิด</th>
                  <th>บังคับ</th>
                  <th>เงื่อนไข</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {section.fields.map((field, fi) => (
                  <tr key={fi}>
                    <td className="mono small">{field.key}</td>
                    <td className="wrap">{field.label}</td>
                    <td className="small">{FIELD_TYPE_LABEL_TH[field.type]}</td>
                    <td>{field.required ? '✓' : ''}</td>
                    <td className="small muted">{field.showIf ? 'มี' : '—'}</td>
                    <td>
                      <div className="btn-row">
                        <button className="link" onClick={() => setEditing({ sectionIndex: si, fieldIndex: fi })}>
                          {readOnly ? 'ดู' : 'แก้ไข'}
                        </button>
                        {!readOnly && (
                          <>
                            <button
                              className="link"
                              disabled={fi === 0}
                              onClick={() => update(moveField(schema, si, fi, -1))}
                            >
                              ↑
                            </button>
                            <button
                              className="link"
                              disabled={fi === section.fields.length - 1}
                              onClick={() => update(moveField(schema, si, fi, 1))}
                            >
                              ↓
                            </button>
                            <button
                              className="link"
                              onClick={() => {
                                if (!confirm(`ลบฟิลด์ "${field.label}"?`)) return;
                                update(removeField(schema, si, fi));
                              }}
                            >
                              ลบ
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!readOnly && (
            <button
              className="secondary"
              style={{ marginTop: 12 }}
              onClick={() => {
                const next = addField(schema, si);
                update(next);
                setEditing({ sectionIndex: si, fieldIndex: next.sections[si]!.fields.length - 1 });
              }}
            >
              + เพิ่มคำถาม
            </button>
          )}
        </div>
      ))}

      {!readOnly && (
        <div className="card">
          <button
            className="secondary"
            onClick={() =>
              update({
                ...schema,
                sections: [
                  ...schema.sections,
                  {
                    key: `section_${schema.sections.length + 1}`,
                    title: `ส่วนที่ ${schema.sections.length + 1}`,
                    fields: [
                      { key: `field_${Date.now().toString(36)}`, type: 'text', label: 'คำถามใหม่' },
                    ],
                  },
                ],
              })
            }
          >
            + เพิ่มส่วน
          </button>
        </div>
      )}

      {!readOnly && (
        <div className="card">
          <div className="btn-row">
            <button onClick={() => void save()} disabled={busy || !dirty}>
              {busy ? 'กำลังบันทึก…' : 'บันทึกฉบับร่าง'}
            </button>
            <button
              className="secondary"
              onClick={() => void publish()}
              disabled={busy || issues.length > 0}
            >
              เผยแพร่
            </button>
            {dirty && <span className="muted small">มีการแก้ไขที่ยังไม่ได้บันทึก</span>}
          </div>
          <p className="muted small" style={{ marginBottom: 0, marginTop: 10 }}>
            <strong>ข้อควรรู้:</strong> ถ้าความหมายของคำถามเปลี่ยน ให้ใช้รหัสฟิลด์ใหม่
            อย่าใช้รหัสเดิม — รายงานเทียบข้ามปีด้วยรหัสฟิลด์ การใช้รหัสเดิมกับความหมายใหม่
            ทำให้ตัวเลขย้อนหลังผิดทันที
          </p>
        </div>
      )}

      <div className="card">
        <h2>ข้อมูลเวอร์ชัน</h2>
        <dl className="detail-list">
          <dt>สร้างเมื่อ</dt>
          <dd>{formatDateTime(form.createdAt)}</dd>
          <dt>เผยแพร่เมื่อ</dt>
          <dd>{formatDateTime(form.publishedAt)}</dd>
          <dt>ใช้ในงานลงพื้นที่</dt>
          <dd>{formatNumber(form.usageCount ?? 0)} รายการ</dd>
        </dl>
      </div>

      {editing && schema.sections[editing.sectionIndex]?.fields[editing.fieldIndex] && (
        <FieldEditor
          field={schema.sections[editing.sectionIndex]!.fields[editing.fieldIndex]!}
          available={availableKeysBefore(schema, editing.sectionIndex, editing.fieldIndex)}
          readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            update(patchField(schema, editing.sectionIndex, editing.fieldIndex, next));
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------ field editor */

function FieldEditor({
  field,
  available,
  readOnly,
  onClose,
  onSave,
}: {
  field: FormField;
  available: string[];
  readOnly: boolean;
  onClose: () => void;
  onSave: (next: FormField) => void;
}) {
  const [draft, setDraft] = useState<FormField>(structuredClone(field));

  function set<K extends keyof FormField>(key: K, value: FormField[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const needsOptions = draft.type === 'select' || draft.type === 'multiselect';
  const isRepeater = draft.type === 'repeater';

  return (
    <Modal title={readOnly ? 'รายละเอียดคำถาม' : 'แก้ไขคำถาม'} onClose={onClose}>
      <div className="form-row">
        <div className="field">
          <label htmlFor="fkey">รหัสฟิลด์ *</label>
          <input
            id="fkey"
            className="mono"
            value={draft.key}
            disabled={readOnly}
            onChange={(e) => set('key', e.target.value)}
          />
          <div className="hint">a-z, 0-9, _ · ใช้เป็นชื่อคอลัมน์ในรายงาน</div>
        </div>
        <div className="field">
          <label htmlFor="ftype">ชนิด *</label>
          <select
            id="ftype"
            value={draft.type}
            disabled={readOnly}
            onChange={(e) => set('type', e.target.value as FieldType)}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {FIELD_TYPE_LABEL_TH[t]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="flabel">คำถาม *</label>
        <input
          id="flabel"
          value={draft.label}
          disabled={readOnly}
          onChange={(e) => set('label', e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="fhelp">คำอธิบายใต้ช่อง</label>
        <input
          id="fhelp"
          value={draft.help ?? ''}
          disabled={readOnly}
          onChange={(e) => set('help', e.target.value || undefined)}
        />
      </div>

      <div className="form-row">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.required ?? false}
            disabled={readOnly}
            onChange={(e) => set('required', e.target.checked || undefined)}
          />
          <span>บังคับกรอก</span>
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.reportable !== false}
            disabled={readOnly}
            onChange={(e) => set('reportable', e.target.checked ? undefined : false)}
          />
          <span>ใช้ในรายงาน</span>
        </label>
      </div>

      {(draft.type === 'number' || draft.type === 'currency' || draft.type === 'photo') && (
        <div className="form-row">
          <div className="field">
            <label htmlFor="fmin">ค่าต่ำสุด / จำนวนรูปขั้นต่ำ</label>
            <input
              id="fmin"
              type="number"
              value={draft.min ?? ''}
              disabled={readOnly}
              onChange={(e) => set('min', e.target.value === '' ? undefined : Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label htmlFor="fmax">ค่าสูงสุด / จำนวนรูปสูงสุด</label>
            <input
              id="fmax"
              type="number"
              value={draft.max ?? ''}
              disabled={readOnly}
              onChange={(e) => set('max', e.target.value === '' ? undefined : Number(e.target.value))}
            />
          </div>
        </div>
      )}

      {needsOptions && (
        <div className="field">
          <label>ตัวเลือก</label>
          {(draft.options ?? []).map((opt, i) => (
            <div className="btn-row" key={i} style={{ marginBottom: 6 }}>
              <input
                className="mono"
                value={opt.value}
                disabled={readOnly}
                placeholder="ค่า"
                style={{ maxWidth: 140 }}
                onChange={(e) =>
                  set(
                    'options',
                    (draft.options ?? []).map((o, j) =>
                      j === i ? { ...o, value: e.target.value } : o,
                    ),
                  )
                }
              />
              <input
                value={opt.label}
                disabled={readOnly}
                placeholder="ข้อความที่แสดง"
                onChange={(e) =>
                  set(
                    'options',
                    (draft.options ?? []).map((o, j) =>
                      j === i ? { ...o, label: e.target.value } : o,
                    ),
                  )
                }
              />
              {!readOnly && (
                <button
                  className="secondary"
                  onClick={() =>
                    set('options', (draft.options ?? []).filter((_, j) => j !== i))
                  }
                >
                  ลบ
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button
              className="secondary"
              onClick={() =>
                set('options', [...(draft.options ?? []), { value: '', label: '' }])
              }
            >
              + เพิ่มตัวเลือก
            </button>
          )}
        </div>
      )}

      {isRepeater && (
        <div className="alert info small">
          ฟิลด์ลูกของรายการซ้ำแก้ผ่านหน้านี้ยังไม่ได้ในเฟส 2 — ใช้แบบฟอร์มตั้งต้นจาก seed
          หรือแก้ผ่าน API โดยตรง
        </div>
      )}

      <div className="field">
        <label htmlFor="fcond">แสดงเมื่อ (เงื่อนไข)</label>
        <div className="form-row">
          <select
            id="fcond"
            value={(draft.showIf as { field?: string })?.field ?? ''}
            disabled={readOnly}
            onChange={(e) =>
              set(
                'showIf',
                e.target.value
                  ? ({ field: e.target.value, eq: true } as FormField['showIf'])
                  : undefined,
              )
            }
          >
            <option value="">แสดงเสมอ</option>
            {available.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          {draft.showIf && (
            <input
              value={String((draft.showIf as { eq?: unknown }).eq ?? '')}
              disabled={readOnly}
              placeholder="ค่าที่ต้องเท่ากับ"
              onChange={(e) => {
                const raw = e.target.value;
                const parsed = raw === 'true' ? true : raw === 'false' ? false : raw;
                set('showIf', {
                  ...(draft.showIf as object),
                  eq: parsed,
                } as FormField['showIf']);
              }}
            />
          )}
        </div>
        <div className="hint">อ้างได้เฉพาะคำถามที่อยู่ก่อนหน้าเท่านั้น</div>
      </div>

      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="secondary" onClick={onClose}>
          {readOnly ? 'ปิด' : 'ยกเลิก'}
        </button>
        {!readOnly && (
          <button type="button" onClick={() => onSave(draft)}>
            ตกลง
          </button>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------- immutable ops */

function patchSection(schema: FormSchema, index: number, patch: Partial<FormSection>): FormSchema {
  return {
    ...schema,
    sections: schema.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)),
  };
}

function patchField(
  schema: FormSchema,
  si: number,
  fi: number,
  next: FormField,
): FormSchema {
  return patchSection(schema, si, {
    fields: schema.sections[si]!.fields.map((f, i) => (i === fi ? next : f)),
  });
}

function addField(schema: FormSchema, si: number): FormSchema {
  const key = `field_${Math.random().toString(36).slice(2, 8)}`;
  return patchSection(schema, si, {
    fields: [...schema.sections[si]!.fields, { key, type: 'text', label: 'คำถามใหม่' }],
  });
}

function removeField(schema: FormSchema, si: number, fi: number): FormSchema {
  return patchSection(schema, si, {
    fields: schema.sections[si]!.fields.filter((_, i) => i !== fi),
  });
}

function moveField(schema: FormSchema, si: number, fi: number, delta: number): FormSchema {
  const fields = [...schema.sections[si]!.fields];
  const target = fi + delta;
  if (target < 0 || target >= fields.length) return schema;
  [fields[fi], fields[target]] = [fields[target]!, fields[fi]!];
  return patchSection(schema, si, { fields });
}

/** เงื่อนไขอ้างได้เฉพาะฟิลด์ที่ประกาศก่อนหน้า — ตรงกับกฎใน validateSchema */
function availableKeysBefore(schema: FormSchema, si: number, fi: number): string[] {
  const keys: string[] = [];
  for (let s = 0; s <= si; s++) {
    const section = schema.sections[s];
    if (!section) break;
    const limit = s === si ? fi : section.fields.length;
    for (let f = 0; f < limit; f++) {
      const field = section.fields[f];
      if (field && field.type !== 'note') keys.push(field.key);
    }
  }
  return keys;
}
